'use client';

import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { Toast, DetailDialogLabels } from './Toast';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogCancel,
} from '../AlertDialog/AlertDialog';
import { TOAST_TYPE } from './styles';
import './Toast.css';

const getAlertDialogVariant = (type: string) => {
  const map: Record<string, 'info' | 'success' | 'warning' | 'error'> = {
    info: 'info',
    success: 'success',
    warning: 'warning',
    error: 'error',
  };
  return map[type];
};

interface ActiveDetail {
  type: string;
  message: string;
  labels?: DetailDialogLabels;
}

interface ToastItem {
  id: number;
  type: string;
  title?: string;
  message: string;
  duration?: number;
  isExiting?: boolean;
  enableDetailDialog?: boolean;
  detailDialogLabels?: DetailDialogLabels;
}

interface ToastContextValue {
  showToast: (options: {
    type?: string;
    title?: string;
    message: string;
    duration?: number;
    enableDetailDialog?: boolean;
    detailDialogLabels?: DetailDialogLabels;
  }) => void;
  info: (message: string, title?: string) => void;
  success: (message: string, title?: string) => void;
  warning: (message: string, title?: string) => void;
  error: (message: string, title?: string) => void;
  neutral: (message: string, title?: string) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

// Global reference for non-React contexts (e.g., axios interceptors)
let globalToastRef: ToastContextValue | null = null;

/**
 * Get the global toast instance for use outside React components.
 * Returns null if ToastProvider is not mounted.
 */
export const getGlobalToast = (): ToastContextValue | null =>
  globalToastRef ?? (window as any).__altusGlobalToast ?? null;

interface ToastProviderProps {
  children: React.ReactNode;
  position?: 'top-right' | 'bottom-center';
  maxToasts?: number;
}

const DEFAULT_DURATIONS = {
  [TOAST_TYPE.INFO]: 4000,
  [TOAST_TYPE.NEUTRAL]: 4000,
  [TOAST_TYPE.SUCCESS]: 4000,
  [TOAST_TYPE.WARNING]: 8000,
  [TOAST_TYPE.ERROR]: 8000
};

export const ToastProvider: React.FC<ToastProviderProps> = ({
  children,
  position = 'bottom-center',
  maxToasts = 3
}) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [activeDetail, setActiveDetail] = useState<ActiveDetail | null>(null);
  const nextIdRef = useRef(0);
  const timeoutRefs = useRef<Map<number, NodeJS.Timeout>>(new Map());
  const lastRemovalTimeRef = useRef(0);

  const removeToast = useCallback((id: number) => {
    const timeout = timeoutRefs.current.get(id);
    if (timeout) {
      clearTimeout(timeout);
      timeoutRefs.current.delete(id);
    }

    // First, mark toast as exiting to trigger exit animation
    setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, isExiting: true } : t)));

    // Then remove after animation completes (300ms as defined in Toast.css)
    setTimeout(() => {
      lastRemovalTimeRef.current = Date.now();
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 300);
  }, []);

  const showToast = useCallback(
    ({
      type = TOAST_TYPE.INFO,
      title,
      message,
      duration,
      enableDetailDialog,
      detailDialogLabels
    }: {
      type?: string;
      title?: string;
      message: string;
      duration?: number;
      enableDetailDialog?: boolean;
      detailDialogLabels?: DetailDialogLabels;
    }) => {
      const id = nextIdRef.current++;
      const finalDuration = duration ?? DEFAULT_DURATIONS[type as keyof typeof DEFAULT_DURATIONS] ?? 4000;

      setToasts((prev) => {
        const newToasts = [...prev, { id, type, title, message, duration: finalDuration, enableDetailDialog, detailDialogLabels }];
        // Limit number of toasts
        if (newToasts.length > maxToasts) {
          const removed = newToasts.shift();
          if (removed) {
            const timeout = timeoutRefs.current.get(removed.id);
            if (timeout) {
              clearTimeout(timeout);
              timeoutRefs.current.delete(removed.id);
            }
          }
        }
        return newToasts;
      });

      // Timer starts from when toast is shown
      const timeout = setTimeout(() => {
        removeToast(id);
      }, finalDuration);

      timeoutRefs.current.set(id, timeout);
    },
    [maxToasts, removeToast]
  );

  const info = useCallback(
    (message: string, title?: string) => {
      showToast({ type: TOAST_TYPE.INFO, title, message });
    },
    [showToast]
  );

  const success = useCallback(
    (message: string, title?: string) => {
      showToast({ type: TOAST_TYPE.SUCCESS, title, message });
    },
    [showToast]
  );

  const warning = useCallback(
    (message: string, title?: string) => {
      showToast({ type: TOAST_TYPE.WARNING, title, message });
    },
    [showToast]
  );

  const error = useCallback(
    (message: string, title?: string) => {
      showToast({ type: TOAST_TYPE.ERROR, title, message });
    },
    [showToast]
  );

  const neutral = useCallback(
    (message: string, title?: string) => {
      showToast({ type: TOAST_TYPE.NEUTRAL, title, message });
    },
    [showToast]
  );

  // Sync global reference for non-React usage and cross-bundle access (pilets)
  useEffect(() => {
    globalToastRef = { showToast, info, success, warning, error, neutral };
    (window as any).__altusGlobalToast = globalToastRef;
    return () => {
      globalToastRef = null;
      (window as any).__altusGlobalToast = null;
    };
  }, [showToast, info, success, warning, error, neutral]);

  const containerClassName =
    position === 'top-right'
      ? 'toast-container fixed top-4 right-4 flex flex-col gap-xs w-full max-w-sm z-[100] pointer-events-auto'
      : 'toast-container fixed bottom-5 left-1/2 transform -translate-x-1/2 flex flex-col gap-xs w-full max-w-sm px-4 z-[100] pointer-events-auto';

  return (
    <ToastContext.Provider
      value={{
        showToast,
        info,
        success,
        warning,
        error,
        neutral
      }}>
      {children}
      <div className={containerClassName}>
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            type={toast.type as any}
            title={toast.title}
            onClose={() => removeToast(toast.id)}
            className={toast.isExiting ? 'toast-exit' : ''}
            enableDetailDialog={toast.enableDetailDialog}
            detailDialogLabels={toast.detailDialogLabels}
            onViewDetails={toast.enableDetailDialog ? () => setActiveDetail({
              type: toast.type,
              message: toast.message,
              labels: toast.detailDialogLabels,
            }) : undefined}>
            {toast.message}
          </Toast>
        ))}
      </div>
      <AlertDialog open={!!activeDetail} onOpenChange={(open) => !open && setActiveDetail(null)}>
        <AlertDialogContent size="md">
          <AlertDialogHeader variant={getAlertDialogVariant(activeDetail?.type || 'info')}></AlertDialogHeader>
          <AlertDialogBody>
            <div className="whitespace-pre-line break-all w-full max-h-[300px] overflow-y-auto">
              {activeDetail?.message}
            </div>
          </AlertDialogBody>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setActiveDetail(null)}>
              {activeDetail?.labels?.close ?? 'Close'}
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextValue => {
  const context = useContext(ToastContext);
  if (!context) {
    const global = getGlobalToast();
    if (global) return global;
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
